import { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'components/ui/toast';
import { useReturnFocus } from 'hooks/useReturnFocus';
import { useUpdateTutorial } from 'hooks/useTutorials';
import { ApiError } from 'utils/unwrapResponse';
import { TUTORIAL_DESCRIPTION_MAX, TUTORIAL_TITLE_MAX, type TutorialVideo } from 'api/tutorialsApi';

interface EditTutorialDialogProps {
  video: TutorialVideo;
  onClose: () => void;
  onSaved: () => void;
}

// The page mounts it only while a video is being edited, so the fields start from that video's values
const EditTutorialDialog = ({ video, onClose, onSaved }: EditTutorialDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const returnFocus = useReturnFocus();
  const { mutateAsync: update } = useUpdateTutorial();
  const [name, setName] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [gone, setGone] = useState(false);
  const busy = useRef(false);
  const details = { title: name.trim(), description: description.trim() };
  const unchanged = details.title === video.title && details.description === video.description;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy.current) return;
    if (!details.title) return setError(t('Enter a name'));
    busy.current = true;
    setSaving(true);
    setError(null);
    try {
      await update({ id: video.id, details });
      toast.success(t('Tutorial updated'));
      onSaved();
    } catch (failure) {
      const deleted = failure instanceof ApiError && failure.status === 404;
      setGone(deleted);
      setError(deleted ? t('This tutorial no longer exists') : t("Couldn't save the changes"));
      setSaving(false);
    } finally {
      busy.current = false;
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="max-w-[480px]" aria-describedby={undefined} {...returnFocus}>
        <DialogHeader>
          <DialogTitle>{t('Edit the tutorial')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-edit-name">{t('Name')}</Label>
            <Input
              id="tutorial-edit-name"
              value={name}
              maxLength={TUTORIAL_TITLE_MAX}
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tutorial-edit-description">{t('Description (optional)')}</Label>
            <Textarea
              id="tutorial-edit-description"
              value={description}
              rows={3}
              maxLength={TUTORIAL_DESCRIPTION_MAX}
              disabled={saving}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={saving || unchanged || gone}>
              {t('Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTutorialDialog;
